import React, { useState, useEffect } from 'react';
import { History, FileText, Calendar, MapPin, Download, Eye } from 'lucide-react';

interface HistoryEntry {
    id: string;
    query: string;
    task_id: string;
    report_type: string;
    region: string;
    target_date: string;
    created_at: string;
}

interface HistoryPanelProps {
    onViewReport: (taskId: string) => void;
    onDownloadReport: (
        taskId: string,
        reportType?: 'no_reference' | 'with_reference'
    ) => void;
}

const HistoryPanel: React.FC<HistoryPanelProps> = ({ onViewReport, onDownloadReport }) => {
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        try {
            setLoading(true);
            const response = await fetch('/api/history');
            if (!response.ok) {
                throw new Error('获取历史记录失败');
            }
            const data = await response.json();
            setHistory(data.history || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : '未知错误');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getReportTypeName = (type: string) => {
        switch (type) {
            case 'operating_analysis':
                return '经营分析报告';
            case 'financial_analysis':
                return '财经分析报告';
            case 'shell_sales_analysis':
                return '壳牌销量归因分析报告';
            default:
                return type;
        }
    };

    // 判断是否是支持两套报告的类型（经营分析和财经分析有标准版和参考版）
    const hasMultipleVersions = (reportType: string) => {
        return reportType === 'operating_analysis' || reportType === 'financial_analysis';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-gray-600">加载历史记录中...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8">
                <div className="text-red-600 mb-2">⚠️ {error}</div>
                <button
                    onClick={loadHistory}
                    className="btn-secondary"
                >
                    重试
                </button>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="flex items-center mb-6">
                <History className="h-6 w-6 text-primary-600 mr-2" />
                <h2 className="text-xl font-bold text-gray-900">历史记录</h2>
            </div>

            {history.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>暂无历史记录</p>
                    <p className="text-sm mt-1">生成报告后将显示在这里</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {history.map((entry) => (
                        <div
                            key={entry.id}
                            className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900 mb-1">
                                        {getReportTypeName(entry.report_type)}
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                        {entry.query}
                                    </p>
                                    <div className="flex items-center text-xs text-gray-500 space-x-4">
                                        {entry.region && (
                                            <div className="flex items-center">
                                                <MapPin className="h-3 w-3 mr-1" />
                                                {entry.region}
                                            </div>
                                        )}
                                        {entry.target_date && (
                                            <div className="flex items-center">
                                                <Calendar className="h-3 w-3 mr-1" />
                                                {entry.target_date}
                                            </div>
                                        )}
                                        <div>
                                            {formatDate(entry.created_at)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => onViewReport(entry.task_id)}
                                    className="btn-secondary flex items-center text-sm"
                                >
                                    <Eye className="h-4 w-4 mr-1" />
                                    查看
                                </button>
                                {hasMultipleVersions(entry.report_type) ? (
                                    <>
                                        <button
                                            onClick={() => onDownloadReport(entry.task_id, "no_reference")}
                                            className="btn-primary flex items-center text-sm"
                                        >
                                            <Download className="h-4 w-4 mr-1" />
                                            下载标准版
                                        </button>
                                        <button
                                            onClick={() => onDownloadReport(entry.task_id, "with_reference")}
                                            className="btn-outline flex items-center text-sm"
                                        >
                                            <Download className="h-4 w-4 mr-1" />
                                            下载参考版
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => onDownloadReport(entry.task_id, "no_reference")}
                                        className="btn-primary flex items-center text-sm"
                                    >
                                        <Download className="h-4 w-4 mr-1" />
                                        下载报告
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {history.length > 0 && (
                <div className="mt-6 text-center">
                    <button
                        onClick={loadHistory}
                        className="btn-secondary"
                    >
                        刷新历史记录
                    </button>
                </div>
            )}
        </div>
    );
};

export default HistoryPanel;
